import ProtectedRoute from '@/components/ProtectedRoute';
import Header from '@/components/Header';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { ArrowLeft, RefreshCw, BarChart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useABTestAnalytics } from '@/hooks/useABTestAnalytics';
import { AnalyticsMetricsGrid } from '@/components/AnalyticsMetricsGrid';
import { SessionPerformanceChart } from '@/components/SessionPerformanceChart';
import { VoterDistributionChart } from '@/components/VoterDistributionChart';

const ABTestingAnalytics = () => {
  const navigate = useNavigate();
  const { metrics, sessionPerformance, voterDistribution, loading, refetch } = useABTestAnalytics();

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <SEO
          title="A/B Testing Analytics | The Bulk Image Generator"
          description="Track performance and insights from your A/B tests."
          noindex={true}
          url="/ab-testing/analytics"
        />
        <Header />
        <main className="container mx-auto px-4 py-8 pt-24">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/ab-testing')}
                className="text-white hover:bg-white/10"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-3xl font-bold text-white">A/B Testing Analytics</h1>
                <p className="text-gray-300 mt-1">
                  Track performance and insights from your A/B tests
                </p>
              </div>
            </div>
            <Button
              onClick={refetch}
              disabled={loading}
              variant="outline"
              size="sm"
              className="border-white/20 text-white bg-white/5 hover:bg-white/10"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-400" />
                <p className="text-gray-300">Loading analytics...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <AnalyticsMetricsGrid metrics={metrics} />
              
              <div className="grid gap-6 md:grid-cols-2">
                <SessionPerformanceChart data={sessionPerformance} />
                <VoterDistributionChart data={voterDistribution} />
              </div>

              {/* Test Variant Gallery Section */}
              {sessionPerformance.length > 0 && (
                <div className="bg-white/5 border border-white/10 rounded-lg p-6">
                  <h2 className="text-2xl font-bold text-white mb-4 flex items-center">
                    <BarChart className="h-6 w-6 mr-2 text-blue-400" />
                    Test Variant Gallery
                  </h2>
                  <p className="text-gray-400 mb-6">
                    View and compare all variants from your A/B tests
                  </p>
                  <div className="grid gap-4">
                    {sessionPerformance.slice(0, 3).map((session) => (
                      <div key={session.sessionId} className="bg-white/5 border border-white/10 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-white font-semibold">{session.sessionName}</h3>
                            <div className="flex gap-4 mt-2 text-sm text-gray-400">
                              <span>{session.variantCount} variants</span>
                              <span>•</span>
                              <span>{session.totalVotes} votes</span>
                              <span>•</span>
                              <span className={session.status === 'active' ? 'text-blue-400' : 'text-yellow-400'}>
                                {session.status}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => navigate('/ab-testing')}
                            className="border-white/20 text-white hover:bg-white/10"
                          >
                            View Details
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sessionPerformance.length === 0 && (
                <div className="text-center py-12 border border-white/10 rounded-lg bg-white/5">
                  <BarChart className="h-12 w-12 mx-auto mb-4 text-gray-500" />
                  <h3 className="text-lg font-semibold mb-2 text-white">No Data Yet</h3>
                  <p className="text-gray-400 mb-4">
                    Create your first A/B test session to see analytics
                  </p>
                  <Button 
                    onClick={() => navigate('/ab-testing')}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    Start A/B Testing
                  </Button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default ABTestingAnalytics;
